/**
 * Projection du comparateur en rapport — chunk 9.AI.1.
 *
 * Fonction **pure** : elle prend la charge du comparateur telle qu'elle sort de
 * `construireComparaison()` et la re-présente. Aucune I/O, aucune seconde requête,
 * aucune règle de comparaison réécrite — sinon l'écran et le rapport finiraient par
 * raconter deux histoires, ce que le cadrage interdit.
 *
 * ## Deux audiences, une projection
 *
 *   `advisor` — ce qu'il faut pour VALIDER : provenance, éléments à revoir, états
 *               d'extraction. Un conseiller relit avant d'envoyer.
 *   `client`  — ce qu'il faut pour COMPRENDRE : aucun enum interne, aucun
 *               identifiant, aucun détail d'extraction, aucune information interne
 *               au cabinet.
 *
 * Le même calcul, deux niveaux de détail. Deux moteurs auraient permis au rapport
 * client de dire autre chose que le rapport conseiller sur les mêmes données.
 *
 * ## Ce que la projection ne fait JAMAIS
 *
 * Elle ne classe pas, ne note pas, ne recommande pas. Elle décrit et compare — la
 * décision reste au conseiller. Aucun « meilleur assureur », aucun score, aucune
 * pondération : hors périmètre par décision PO, et le rester est un invariant testé.
 */

import { RECEPTION_WITHOUT_COLUMN, type ReceptionState } from "./cell-status.js";
import { fieldComparability, guaranteeFamilyOf } from "./field-registry.js";

export const REPORT_AUDIENCES = ["advisor", "client"] as const;
export type ReportAudience = (typeof REPORT_AUDIENCES)[number];

/**
 * Ce qu'une cellule devient dans un rapport.
 *
 * Les cinq sémantiques 9.AB.1 sont **conservées telles quelles** — elles sont la
 * source de vérité. `readable` est leur traduction en concept lisible, calculée
 * ici pour que le libellé ne soit pas reconstruit dans chaque surface.
 */
export type ReportCell = {
  /** La sémantique 9.AB.1, inchangée. Absente de la sortie CLIENT. */
  cell_status?: string;
  /** Clé de libellé lisible — jamais l'enum brut côté client. */
  readable: "offered" | "not_offered" | "not_provided" | "unknown" | "to_verify" | "unreadable";
  value: unknown;
  unit: string | null;
  /** Provenance — conseiller uniquement. */
  evidence?: Record<string, unknown> | null;
  /** Vrai si la valeur retenue vient d'une correction humaine. Conseiller. */
  human_override?: boolean;
  /** Vrai si cette cellule demande encore une validation. Les DEUX audiences. */
  needs_review: boolean;
};

/**
 * Traduit une sémantique 9.AB.1 en concept lisible.
 *
 * `not_detected` et `not_offered` restent **distincts** : le premier dit « on n'a
 * pas trouvé », le second « l'assureur ne l'offre pas, et voici la preuve ». Les
 * confondre transformerait une lacune de lecture en refus de couverture.
 */
export function readableStatus(cellStatus: string): ReportCell["readable"] {
  switch (cellStatus) {
    case "value":
      return "offered";
    case "not_offered":
      return "not_offered";
    case "not_detected":
      return "unknown";
    // 9.AM.1 — « la proposition ne le dit pas » n'est ni « on n'a pas trouvé »
    // (`unknown`), ni « l'assureur ne l'offre pas » (`not_offered`). Les trois
    // envoient le conseiller — et le client — vers des conclusions différentes.
    case "not_provided":
      return "not_provided";
    case "needs_review":
      return "to_verify";
    case "extraction_failed":
      return "unreadable";
    default:
      // Un statut inconnu se lit « à vérifier », jamais « offert » : présenter une
      // garantie comme acquise sur une sémantique qu'on ne comprend pas serait la
      // seule erreur véritablement coûteuse de ce tableau.
      return "to_verify";
  }
}

/**
 * Ce qu'un assureur sans proposition devient dans un rapport.
 *
 * Trois valeurs, et pas une de plus. Un état de réception inconnu se lit
 * « réponse attendue » — le seul repli qui n'affirme rien : dire « a décliné »
 * sur un état qu'on ne comprend pas prêterait un refus à un assureur qui n'en a
 * pas exprimé.
 */
export type ProcessReadable = "awaiting" | "declined" | "expired";

export function readableReception(state: string): ProcessReadable {
  switch (state) {
    case "declined":
      return "declined";
    case "expired":
      return "expired";
    default:
      return "awaiting";
  }
}

export type ReportColumn = {
  submission_id?: string;
  insurer_code: string;
  insurer_name: string;
  /** Conseiller uniquement — un client n'a pas à lire un statut d'extraction. */
  reception_state?: ReceptionState;
};

export type ReportRow = {
  field_key: string;
  guarantee_family: string | null;
  cells: Record<string, ReportCell>;
};

export type ReportProjection = {
  audience: ReportAudience;
  columns: ReportColumn[];
  rows: ReportRow[];
  /**
   * Assureurs sollicités SANS proposition — contexte de processus, pas colonnes.
   *
   * `readable` est un ensemble FERMÉ de trois valeurs, présent dans les deux
   * audiences ; `reception_state` est l'enum 9.AB.1 brut et reste au conseiller.
   * Même raison que pour les cellules : le jour où l'enum gagne un état, un
   * document client ne doit pas se mettre à porter un mot que personne n'a pesé.
   */
  process_context: {
    insurer_code: string;
    readable: ProcessReadable;
    reception_state?: ReceptionState;
  }[];
  needs_review_count: number;
  has_human_overrides: boolean;
  /**
   * Les propositions qui ont alimenté le rapport — **conseiller uniquement**.
   *
   * C'est de la traçabilité, pas du contenu de document. Mon premier jet la sortait
   * dans les deux audiences : le rapport client portait donc des identifiants
   * internes, ce qui est une fuite d'infrastructure et n'apprend rien au client. La
   * route calcule ses références depuis la charge du comparateur, elle n'a pas
   * besoin que la projection les lui rende.
   */
  submission_ids?: string[];
};

/**
 * La charge telle que la produit le comparateur.
 *
 * Exportée pour que l'API puisse la nommer plutôt que la caster à l'aveugle —
 * la lecture reste structurelle, donc un champ qui disparaîtrait côté
 * comparateur casserait ici, à la compilation, et pas dans un rapport livré.
 */
export type ComparisonCharge = {
  submissions?: {
    id: string;
    insurer_code: string;
    insurer_name: string;
    received_at?: string | null;
    created_at?: string;
  }[];
  rows?: {
    field_key: string;
    hidden_by_default?: boolean;
    cells: Record<string, Record<string, unknown>>;
  }[];
  insurers?: {
    insurer_code: string;
    reception_state: string;
    submission_id: string | null;
    has_column: boolean;
  }[];
};

/**
 * Projette la charge du comparateur en rapport.
 *
 * Les lignes masquées par défaut (techniques, non comparatives) sont **exclues** :
 * un rapport présentable ne s'ouvre pas sur des lignes que l'écran replie. Le
 * masquage vient de `rowHiddenByDefault`, donc la même règle qu'à l'écran.
 */
export function projectReport(
  charge: ComparisonCharge,
  audience: ReportAudience,
): ReportProjection {
  const pourConseiller = audience === "advisor";
  const submissions = (charge.submissions ?? []).filter((s) => s?.id);
  const parSubmission = new Map(submissions.map((s) => [s.id, s]));

  const receptionParSub = new Map<string, string>();
  for (const i of charge.insurers ?? []) {
    if (i?.submission_id) receptionParSub.set(i.submission_id, i.reception_state);
  }

  const columns: ReportColumn[] = submissions.map((s) => ({
    // Un identifiant technique n'a rien à faire dans un document remis au client.
    ...(pourConseiller ? { submission_id: s.id } : {}),
    insurer_code: s.insurer_code,
    insurer_name: s.insurer_name,
    ...(pourConseiller
      ? { reception_state: (receptionParSub.get(s.id) ?? "ready") as ReceptionState }
      : {}),
  }));

  let needsReview = 0;
  let overrides = false;
  const rows: ReportRow[] = [];

  for (const ligne of charge.rows ?? []) {
    // Une ligne repliée à l'écran n'a pas à ouvrir un rapport.
    if (ligne?.hidden_by_default === true) continue;
    if (!ligne?.field_key) continue;
    // Les débris techniques d'extraction (`extra_*`) n'ont rien à comparer : les
    // présenter suggérerait une différence là où il n'y en a pas de mesurable.
    // Les métadonnées de document sont conservées pour le CONSEILLER — elles
    // servent à valider — et écartées du rapport client, qui n'a pas à lire
    // « assureur détecté » ou « langue du document ».
    const nature = fieldComparability(ligne.field_key);
    if (nature === "technical") continue;
    if (!pourConseiller && nature === "document_metadata") continue;

    const cells: Record<string, ReportCell> = {};
    for (const [subId, brute] of Object.entries(ligne.cells ?? {})) {
      const sub = parSubmission.get(subId);
      if (!sub) continue;
      const statut = String(brute.cell_status ?? "needs_review");
      const aRevoir = Boolean(brute.needs_review) || statut === "needs_review";
      if (aRevoir) needsReview += 1;
      // Deux provenances humaines DISTINCTES, et il faut les deux.
      //
      //   `is_human_override`        — la valeur de CETTE cellule a été corrigée.
      //   `evidence.human_override`  — la preuve négative retenue vient d'une
      //                                correction sur un champ frère (9.AH.1).
      //
      // Ne lire que la seconde était un angle mort : un dossier où un courtier
      // avait corrigé trois montants annonçait « aucune correction humaine » au
      // relecteur, ce qui est l'inverse de ce qu'il doit savoir avant d'envoyer.
      const parHumain =
        Boolean(brute.is_human_override) ||
        Boolean((brute.evidence as { human_override?: boolean } | null)?.human_override);
      if (parHumain) overrides = true;

      // La CLÉ de colonne : jamais un identifiant côté client.
      const cle = pourConseiller ? subId : sub.insurer_code;
      cells[cle] = {
        ...(pourConseiller ? { cell_status: statut } : {}),
        readable: readableStatus(statut),
        value: brute.value ?? null,
        unit: (brute.unit as string | null) ?? null,
        ...(pourConseiller
          ? {
              evidence: (brute.evidence as Record<string, unknown> | null) ?? null,
              human_override: parHumain,
            }
          : {}),
        // Visible dans les DEUX versions. Le cadrage l'impose : « ne masque pas
        // silencieusement ces éléments ». Un client a le droit de savoir qu'une
        // ligne n'est pas encore vérifiée.
        needs_review: aRevoir,
      };
    }
    if (Object.keys(cells).length === 0) continue;
    rows.push({
      field_key: ligne.field_key,
      guarantee_family: guaranteeFamilyOf(ligne.field_key),
      cells,
    });
  }

  // Les assureurs sans proposition : contexte de PROCESSUS, jamais une colonne.
  // Une colonne vide se lirait « aucune garantie offerte », et cette lecture est
  // indétectable — rien dans le document ne dirait qu'elle est fausse.
  //
  // Deux resserrements que le smoke réel a rendus obligatoires, tous deux propres
  // au RAPPORT — le bandeau de l'écran, lui, doit garder le détail par destinataire.
  //
  //   1. La charge liste un destinataire par SOLLICITATION. Sur un dossier réel,
  //      « Canada Vie — réponse attendue » sortait TROIS fois : trois demandes au
  //      même assureur. Dans un document, c'est du bruit qui se lit comme trois
  //      assureurs distincts.
  //   2. Un assureur qui a déjà une COLONNE ne peut pas figurer ici. Sur ce même
  //      dossier, quatre assureurs apparaissaient à la fois en colonne, avec des
  //      valeurs, et en « sollicité sans proposition reçue » — parce que leurs
  //      propositions historiques ne portent pas le lien 9.AG.1 vers le créneau.
  //      Le document se contredisait lui-même, sous les yeux d'un client.
  const codesAvecColonne = new Set(columns.map((c) => c.insurer_code));
  const dejaVus = new Set<string>();
  const process_context: ReportProjection["process_context"] = [];
  for (const i of charge.insurers ?? []) {
    if (!i?.insurer_code) continue;
    if (!(RECEPTION_WITHOUT_COLUMN as readonly string[]).includes(i.reception_state)) continue;
    if (codesAvecColonne.has(i.insurer_code)) continue;
    if (dejaVus.has(i.insurer_code)) continue;
    dejaVus.add(i.insurer_code);
    process_context.push({
      insurer_code: i.insurer_code,
      readable: readableReception(i.reception_state),
      ...(pourConseiller ? { reception_state: i.reception_state as ReceptionState } : {}),
    });
  }

  return {
    audience,
    columns,
    rows,
    process_context,
    needs_review_count: needsReview,
    has_human_overrides: overrides,
    ...(pourConseiller ? { submission_ids: submissions.map((s) => s.id) } : {}),
  };
}
